// ========================================
// FILE: src/components/Dashboard/ScreeningList.jsx (Enhanced)
// ========================================
import React, { useState } from 'react';
import { FileText, Clock, CheckCircle, AlertCircle, TrendingUp, Calendar, Users, ChevronRight } from 'lucide-react';

const ScreeningList = ({ screenings, loading, onScreeningClick }) => {
  const [filter, setFilter] = useState('all'); // 'all', 'completed', 'processing'

  const getFilteredScreenings = () => {
    switch(filter) {
      case 'completed':
        return screenings.filter(s => s.status === 'COMPLETED');
      case 'processing':
        return screenings.filter(s => s.status === 'PROCESSING');
      default:
        return screenings;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      COMPLETED: {
        bg: 'bg-green-100',
        text: 'text-green-700',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Completed'
      },
      PROCESSING: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        icon: <Clock className="w-4 h-4 animate-spin" />,
        label: 'Processing'
      },
      FAILED: {
        bg: 'bg-red-100',
        text: 'text-red-700',
        icon: <AlertCircle className="w-4 h-4" />,
        label: 'Failed'
      }
    };
    return badges[status] || badges.COMPLETED;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">📊 Screening History</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-20 bg-gray-200 rounded-xl"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const filteredScreenings = getFilteredScreenings();

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Screening History</h2>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'completed', 'processing'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                filter === f
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && (
                <span className="ml-1 text-xs">
                  ({screenings.filter(s => s.status === f.toUpperCase()).length})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {filteredScreenings.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-10 h-10 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg mb-2">
            {filter === 'all'
              ? 'No screenings yet'
              : `No ${filter} screenings`}
          </p>
          <p className="text-gray-400 text-sm">
            {filter === 'all'
              ? 'Upload your first job description and resumes to get started!'
              : 'Try selecting a different filter'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredScreenings.map((screening) => {
            const statusInfo = getStatusBadge(screening.status);

            return (
              <div
                key={screening.id}
                onClick={() => onScreeningClick(screening.id)}
                className="group border-2 border-gray-100 rounded-xl p-5 hover:border-purple-300 hover:shadow-lg transition-all cursor-pointer bg-gradient-to-r from-white to-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Icon */}
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-lg mb-1 truncate group-hover:text-purple-600 transition">
                        {screening.jobTitle}
                      </h3>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{screening.totalResumes} candidates</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(screening.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${statusInfo.bg} ${statusInfo.text} font-medium`}>
                      {statusInfo.icon}
                      <span className="text-sm">{statusInfo.label}</span>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition" />
                  </div>
                </div>

                {/* Progress Bar for Processing */}
                {screening.status === 'PROCESSING' && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                      <span>Processing {screening.processedResumes}/{screening.totalResumes} resumes</span>
                      <span>{Math.round((screening.processedResumes / screening.totalResumes) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(screening.processedResumes / screening.totalResumes) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Stats */}
      {screenings.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{screenings.length}</div>
              <div className="text-sm text-gray-500">Total Screenings</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {screenings.filter(s => s.status === 'COMPLETED').length}
              </div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {screenings.reduce((acc, s) => acc + s.totalResumes, 0)}
              </div>
              <div className="text-sm text-gray-500">Candidates Screened</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreeningList;